// Angular
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
// RxJs & Lodash
import { Observable } from "rxjs";
import { catchError } from 'rxjs/operators';
import { get, map, replace, toUpper, trim } from "lodash";
// Utils
import { GlobalErrorHandler, HttpUtilsService } from '@core/utils';
// Constants
import { API_URL, IMAGE_BASE_URL, ORIGIN_FILTER, ORIGIN_ITEM, ORIGIN_QUICK_CART, ORIGIN_SUBJECT, PLP_DEAFULT_SORT_COLUMN, PLP_DEAFULT_SORT_DIRECTION } from '@constants';
// Models
import { IProductListParams } from "../models";
// Environment
import { environment } from "@environment";

// Common header
const HEADER_ACCEPT_JSON = { accept: 'application/json' };

@Injectable({
    providedIn: 'root'
})
export class PartSearchService {

    // Private
    private readonly httpUtilsService = inject(HttpUtilsService);
    private readonly globalErrorHandler = inject(GlobalErrorHandler);
    private platformId = inject<Object>(PLATFORM_ID);
    private isBrowser: boolean;

    /**
     * Creates an instance of PartSearchService.
     * @param {HttpUtilsService} httpUtilsService
     * @param {GlobalErrorHandler} globalErrorHandler
     * @memberof PartSearchService
     */
    constructor() {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    /**
     * Get Makes List
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getMakesList(): Observable<any> {
        const payload = {
            search: {
                sm: 1,
            },
            sort: {
                column: 'vt1',
                direction: 'asc',
            },
            context: {
                exist: true,
                popular: 50,
            },
            seo: {
                url: '/{lang}/pb/{label_search}/{label_car_parts}',
            },
        };
        return this.httpUtilsService
            .postData(API_URL.MAKES_LIST_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-car-part-make-list', error, true))
            );
    }

    /**
     * Get Makes > Models
     * @param {string} vc1
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getModels(vc1: string): Observable<any> {
        const payload = {
            search: {
                sm: 1,
                filter: {
                    vc1,
                },
            },
            sort: {
                column: 'popular',
                direction: 'desc',
            },
            context: {
                exist: true,
                popular: 50,
            },
            seo: {
                url: '/{lang}/pb/{label_search}/{label_car_parts}/s1/{vc1}',
            },
        };
        return this.httpUtilsService
            .postData(API_URL.MODELS_LIST_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-car-part-models-list', error, true))
            );
    }

    /**
     * Get Categories
     * @param {*} params
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getCategories(params: any): Observable<any> {
        const filter = this._getPayloadFilter(params);
        const payload = {
            search: {
                sm: params?.sm,
                ...filter,
            },
            context: {
                exist: true,
                image: {
                    width: 300,
                    height: null,
                    quality: null,
                },
            },
            seo: {
                url: params?.path,
            },
        };

        return this.httpUtilsService
            .postData(API_URL.CATEGORY_LIST_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-car-part-categories', error, true))
            );
    }

    /**
     * Get Category Product
     * @param {*} params
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getProducts(params: any): Observable<any> {
        const filter = this._getPayloadFilter(params);
        const payload = {
            search: {
                sm: params?.sm,
                ...filter,
            },
            context: {
                exist: true,
                popular: 20,
                image: {
                    width: 300,
                    height: null,
                    quality: null,
                },
            },
            seo: {
                url: params?.path
            },
        };
        return this.httpUtilsService
            .postData(API_URL.CATEGORY_PRODUCTS_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-car-part-category-products', error, true))
            );
    }

    /**
     * Get Reg Nr Details
     * @param {*} payload
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getRegnrDetails(payload: any): Observable<any> {
        return this.httpUtilsService
            .postData(API_URL.REG_NR_SEARCH_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-reg-nr-details', error, true))
            );
    }

    /**
     * Get Product Details
     * @param {IProductListParams} params
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getProductsList(params: IProductListParams): Observable<any> {
        const payload = {
            search: {
                sm: params?.sm,
                filter:
                {
                    code_vc: params?.vehicle_post_id ? undefined : (params?.code_vc_make_model ? [[params?.code_vc_make_model, params?.code_vc_myft]] : undefined),
                    find: params?.find ? params?.find : undefined,
                    vc1: params?.vehicle_post_id ? undefined : (params?.vt1 ?? undefined),
                    vc2: params?.vt2 ? [params?.vt2] : undefined,
                    pc1: params?.pc1 ? params?.pc1 : undefined,
                    pc: params?.pc ? [[params?.pc, params?.pos]] : undefined,
                    query: params?.query ? params.query : undefined,
                    part_item: params?.part_item ? params?.part_item : undefined,
                    part_type_id: params?.part_type_id.length > 0 ? params?.part_type_id : undefined,
                    part_quality_code: params?.part_quality_code.length > 0 ? params?.part_quality_code : undefined,
                    company_id: params.company_id?.length > 0 ? params?.company_id : undefined,
                    company_country_id: params?.country_id.length > 0 ? params?.country_id : undefined,
                    company_county_id: params?.county_id.length > 0 ? params.county_id : undefined,
                    vpost_id: params?.vehicle_post_id ? params.vehicle_post_id : undefined,
                    vehicle_model_year_from: params?.from_model_year,
                    vehicle_model_year_to: params?.to_model_year,
                    vehicle_milage_km_from: params?.from_milage_km,
                    vehicle_milage_km_to: params?.to_milage_km,
                    company_group_id: params?.group_id ? [Number(params.group_id)] : undefined,
                    exact: params?.exact === 1 ? 1 : undefined
                },
            },
            context: {
                shipping: this.isBrowser,
                price: this.isBrowser,
                booking: this.isBrowser,
                logger: this.isBrowser, // false: Server, true: client
                origin: ORIGIN_SUBJECT,
                image: {
                    width: 256,
                    height: null,
                    quality: null,
                },
            },
            sort: { column: params?.sort_column, direction: params?.sort_direction },
            pagination: {
                offset: params?.offset,
                limit: params?.limit
            },
            seo: {
                url: params?.path
            },
        };

        return this.httpUtilsService
            .postData(API_URL.PRODUCTS_LIST_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-products-list', error, true))
            );
    }

    /**
     * Get PLP Filters
     * @param {*} params
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getProductFilters(params: any): Observable<any> {
        const pos = params?.pos ? [params?.pos] : [];
        const payload = {
            search: {
                sm: params?.sm,
                filter: {
                    code_vc: params?.code_vc_make_model ? [[params?.code_vc_make_model, params?.code_vc_myft]] : undefined,
                    pc1: params?.pc1 ? params?.pc1 : undefined,
                    vc1: params?.vt1 ? params?.vt1 : undefined,
                    vc2: params?.vt2 ? [params?.vt2] : undefined,
                    pc: params?.pc ? [[params?.pc, pos]] : undefined,
                    vpost_id: params?.vehicle_post_id ? params?.vehicle_post_id : undefined,
                    query: params?.query ? (params.query).replace(/[{},@!`*]/g, '') : params?.part_item ? (toUpper(params?.partitem).replace(/\D+/g, '').trim()) : undefined,
                    exact: params?.exact ? 1 : 0,
                },
            },
            context: {
                origin: ORIGIN_FILTER
            }
        };
        return this.httpUtilsService
            .postData(API_URL.FILTERS_LIST_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-product-filters-list', error, true))
            );
    }

    /**
     * Get Product Details
     * @param {*} params
     * @return {*}  {Observable<any>}
     * @memberof PartSearchService
     */
    public getProductDetails(params: any): Observable<any> {

        // Check if path is DF(tyre & rims)
        const urlPath = params?.sourcePath === 'DF' ? '/{lang}/df/{label_tyres_rims}/{tyre_width}-{tyre_aspect_ratio}-{tyre_wheel_diameter_inches}/ID-{id}' : '/{lang}/pb/{label_search}/{label_car_parts}/s1/{vc1}/{vc}/{vcx}/{pc1}/{pc}/{pos}/ID-{id}/{oem}';

        const payload = {
            search: {
                sm: 13,
                filter: {
                    ppost_id: [params?.id],
                },
            },
            context: {
                shipping: true,
                booking: true,
                price: true,
                logger: this.isBrowser, // false: Server, true: client
                origin: ORIGIN_ITEM,
                company_full_review: true, // To get company full review data
                image: {
                    width: 1024,
                    height: null,
                    quality: null,
                },
            },
            seo: {
                url: urlPath,
            },
        };

        return this.httpUtilsService
            .postData(API_URL.PRODUCTS_LIST_URL, payload, null, { ...HEADER_ACCEPT_JSON, 'cache-control': 'no-cache' })
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-product-details', error, true))
            );
    }

    /**
     * Get Saved Parts Products List
     * @param {Array<number>} ppost_id
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getSavedPartsProductList(ppost_id: Array<number>, origin: string): Observable<any> {
        const payload = {
            search: {
                sm: 13,
                filter: {
                    ppost_id,
                },
            },
            context: {
                shipping: origin === ORIGIN_QUICK_CART ? false : true,
                booking: true,
                price: origin === ORIGIN_QUICK_CART ? false : true,
                logger: false,
                origin,
                image: {
                    width: 256,
                    height: null,
                    quality: null,
                },
            },
            sort: {
                column: PLP_DEAFULT_SORT_COLUMN,
                direction: PLP_DEAFULT_SORT_DIRECTION,
            },
            pagination: {
                offset: 0,
                limit: ppost_id.length + 1,
            },
            seo: {
                url: '/{lang}/saved-parts',
            },
        };

        return this.httpUtilsService
            .postData(API_URL.PRODUCTS_LIST_URL, payload, null, { ...HEADER_ACCEPT_JSON, 'cache-control': 'no-cache' })
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-saved-products-list', error, true))
            );
    }

    /**
     * Get customer cart by searching email or cart key
     * @param {string} q (query)
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getCartByEmailOrCartId(payload: any): Observable<any> {
        return this.httpUtilsService
            .postData(API_URL.FIND_CART_BY_EMAIL_OR_ID_URL, payload, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-customer-cart-by-email-or-cart-id', error, true))
            );
    }

    /**
     * Get FMS Chat Users List
     * @param {Array<number>} companyIds
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getFMSUserList(companyIds: Array<number>): Observable<any> {
        return this.httpUtilsService
            .postData(API_URL.FMS_USERS_URL, { company_id: companyIds }, null, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-fms-user-list', error, true))
            );
    }

    /**
     * Delete Cart
     * @param {*} payload
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public deleteCart(payload: any): Observable<any> {
        return this.httpUtilsService
            .deleteDataWithHttpResponse(API_URL.CREATE_CART_URL, null, null, payload)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('delete-cart-via-key', error, true))
            );
    }

    /**
     * Create PickUp Cart
     * @param {*} payload
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public createPickUpCart(payload: any): Observable<any> {
        return this.httpUtilsService
            .postData(API_URL.CREATE_CART_URL, payload, null)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('send-inquiry-to-seller', error, true))
            );
    }

    /**
     * Send statistic logs to server
     * @param {*} payload
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public sendStatsLog(payload: any): Observable<any> {
        return this.httpUtilsService
            .postData(API_URL.STATS_LOG_URL, payload, null)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('send-statistic-log', error, true))
            );
    }

    /**
     * Get Company Review
     * @param {*} payload
     * @returns {Observable<any>}
     * @memberof PartSearchService
     */
    public getCompanyReview(params: { company_id: number; date: string }): Observable<any> {
        return this.httpUtilsService
            .getData(API_URL.COMPANY_REVIEW_URL, params, HEADER_ACCEPT_JSON)
            .pipe(
                catchError((error: any) => this.globalErrorHandler.handleError('get-company-reviews', error, true))
            );
    }

    /**
    * Get Item List Schema
    * @param {Array<any>} products
    * @param {Function} getNameCallback - A callback function to construct the name based on the component's need.
    * @returns {Array<any>}
    */
    public getItemListElement(products: Array<any>, getNameCallback: (item: any) => string): Array<any> {

        return map(products, (item: any, index: number) => {
            const price = get(item, 'shop.raw.price');
            const shippingPrice = get(item, 'shipping.raw.item_price');

            // Skip product if price or shipping is null
            if (price === null || shippingPrice === null) {
                return null; // Mark for filtering
            }

            return {
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@type': 'Product',
                    name: getNameCallback(item), // Customizable product name based on the component.
                    image: get(item, 'part.image', []).length > 0 ? get(item, 'part.image', []).map((img: any) => IMAGE_BASE_URL + img) : IMAGE_BASE_URL + '/assets/media/general/no-image.jpg',
                    sku: get(item, 'id', null),
                    mpn: get(item, 'part.original.number', null),
                    brand: {
                        '@type': 'Brand',
                        name: get(item, 'code.vehicle.code_vc.text'),
                    },
                    offers: {
                        '@type': 'Offer',
                        priceCurrency: get(item, 'shop.raw.currency'),
                        price,
                        itemCondition: this.partCondition(get(item, 'part.general.type_id')),
                        availability: 'https://schema.org/InStock',
                        seller: {
                            '@type': 'Organization',
                            name: get(item, 'company.name'),
                            telephone: get(item, 'company.phone.international', '9999999999')
                        },
                        shippingDetails: {
                            '@type': 'OfferShippingDetails',
                            shippingRate: {
                                '@type': 'MonetaryAmount',
                                value: shippingPrice,
                                currency: get(item, 'shipping.raw.item_currency', 'SEK'),
                            },
                            shippingDestination: {
                                '@type': 'DefinedRegion',
                                addressCountry: 'SE',
                            },
                            deliveryTime: {
                                '@type': 'ShippingDeliveryTime',
                                businessDays: {
                                    '@type': 'OpeningHoursSpecification',
                                    dayOfWeek: [
                                        'https://schema.org/Monday',
                                        'https://schema.org/Tuesday',
                                        'https://schema.org/Wednesday',
                                        'https://schema.org/Thursday',
                                        'https://schema.org/Friday',
                                    ],
                                },
                                cutoffTime: '12:00:15Z',
                                handlingTime: {
                                    '@type': 'QuantitativeValue',
                                    minValue: 1,
                                    maxValue: 2,
                                    unitCode: 'd',
                                },
                                transitTime: {
                                    '@type': 'QuantitativeValue',
                                    minValue: 1,
                                    maxValue: 5,
                                    unitCode: 'd',
                                },
                            },
                        },
                    },
                },
            };
        }).filter(item => item !== null); // Filter out null values
    }

    /**
     * Return Part Item Condition
     * ie. Used, New, Refurbished
     * @returns {string}
     * @memberof PartSearchService
     */
    public partCondition(typeId: number): string {
        switch (typeId) {
            case 2:
                return 'https://schema.org/RefurbishedCondition';
            case 3:
                return 'https://schema.org/NewCondition';
            default:
                return 'http://schema.org/UsedCondition';
        }
    }

    /**
     * Get Payload Filter for Normal and Other Route Path
     * @example Route - "/sv-se/ov/Sök/Bildelar/Övrigt/a/a"
     * @private
     * @param {*} params
     * @returns
     * @memberof PartSearchService
     */
    private _getPayloadFilter(params: any) {
        switch (params?.sm) {
            case 17:
                return {
                    filter: {
                        vc1: params?.vt1,
                        pc1: params?.pc1 ?? undefined,
                        query: params?.query ?? undefined,
                        find: params?.find ?? undefined,
                    },
                };
            case 19:
                return {
                    filter: {
                        vc1: params?.vt1,
                        vc2: params?.vt2 ? [params?.vt2] : undefined,
                        pc1: params?.pc1 ?? undefined,
                        query: params?.query ?? undefined,
                        find: params?.find ?? undefined
                    },
                };
            default:
                // Check if code_vc_make_model exists
                if (params?.code_vc_make_model) {
                    // Return payload with filter for code_vc_make_model conditions
                    return {
                        filter: {
                            code_vc: [[params?.code_vc_make_model, params?.code_vc_myft]],
                            pc1: params?.pc1 ?? undefined,
                            query: params?.query ?? undefined,
                            find: params?.find ?? undefined
                        },
                    };
                } else {
                    // Check if pc1 exists
                    if (params?.pc1) {
                        // Return payload with filter for pc1 conditions
                        return { filter: { pc1: params?.pc1, query: params?.query ?? undefined, find: params?.find ?? undefined } };
                    } else {
                        // Return undefined if no specific conditions are met
                        return { filter: { query: params?.query ?? undefined, find: params?.find ?? undefined } };
                    }
                }
        }
    }

}